import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LoadingSpinner from "../LoadingSpinner";
import { CategoryState } from "../../utils/CategoryState";
import { RequestState } from "../../utils/RequestState";
import styles from "./Modal.module.scss";
import SonicLink from "../routing/SonicLink";

type Props = {
  children: React.ReactNode;
  closeModal: () => void;
  handler?: () => void;

  categoryState: CategoryState;
  requestState?: RequestState;

  headText: string;

  buttonText?: string | JSX.Element;
  linkText?: string;
  to?: string;
  error?: string;
};

const Modal: React.FC<Props> = (props) => {
  const {
    children,
    closeModal,
    handler,
    categoryState,
    requestState,
    headText,
    buttonText,
    linkText,
    to,
    error,
  } = props;

  const genHeadIcon = () => {
    switch (requestState) {
      case RequestState.Success:
        return <FontAwesomeIcon icon={["fal", "check-circle"]} />;
      default:
        switch (categoryState) {
          case CategoryState.Delete:
            return <FontAwesomeIcon icon={["fal", "trash-alt"]} />;
          case CategoryState.Add:
            return <FontAwesomeIcon icon={["fal", "user-plus"]} />;
          case CategoryState.Approval:
            return <FontAwesomeIcon icon={["fal", "circle"]} />;
          case CategoryState.Fix:
            return <FontAwesomeIcon icon={["fal", "times"]} />;
          case CategoryState.Memo:
            return <FontAwesomeIcon icon={["fal", "file"]} />;
          case CategoryState.Edit:
            return <FontAwesomeIcon icon={["fal", "edit"]} />;
          case CategoryState.Sort:
            return <FontAwesomeIcon icon={["fal", "arrow-up-arrow-down"]} />;
          case CategoryState.Confirm:
            return <FontAwesomeIcon icon={["fas", "megaphone"]} />;
          default:
            null;
        }
    }
  };

  const genHeadColor = () => {
    switch (requestState) {
      case RequestState.Success:
        return "#28A745";
      case RequestState.Failed:
        return "#DC3545";
      default:
        switch (categoryState) {
          case CategoryState.Delete:
          case CategoryState.Fix:
            return "#DC3545";
          case CategoryState.Add:
          case CategoryState.Edit:
          case CategoryState.Sort:
          case CategoryState.Confirm:
            return "#F8AA27";
          case CategoryState.Approval:
            return "#007BFF";
          default:
            return "#232323";
        }
    }
  };

  const genBottomBtnColor = () => {
    switch (categoryState) {
      case CategoryState.Delete:
      case CategoryState.Fix:
        return "#DC3545";
      case CategoryState.Add:
      case CategoryState.Edit:
      case CategoryState.Sort:
      case CategoryState.Confirm:
        return "#F8AA27";
      case CategoryState.Approval:
        return "#007BFF";
      default:
        return "#F8AA27";
    }
  };

  return (
    <div className={styles["f-modal"]}>
      <div className={styles["f-modal__wrapper"]}>
        {requestState === RequestState.Doing ? (
          <LoadingSpinner space={1} admin_page={true} />
        ) : (
          <>
            <div className={styles["f-modal__wrapper-head"]}>
              <div
                className={styles["f-modal__wrapper-head__title"]}
                style={{ color: genHeadColor() }}
              >
                {genHeadIcon()}
                <span>{headText}</span>
              </div>
              <FontAwesomeIcon
                icon={["fal", "times"]}
                onClick={() => closeModal()}
                className={styles["f-modal__wrapper-head__close"]}
              />
            </div>
            {children ? (
              <div className={styles["f-modal__wrapper-content"]}>
                {children}
              </div>
            ) : null}
            {error ? (
              <div className={styles["f-modal__wrapper-error"]}>{error}</div>
            ) : null}
            <div
              className={
                buttonText
                  ? `${styles["f-modal__wrapper-bottom"]}`
                  : `${styles["f-modal__wrapper-bottom"]} ${styles["result"]}`
              }
            >
              <span
                className={`${styles["f-modal__wrapper-bottom__btn"]} ${styles["close"]}`}
                onClick={() => closeModal()}
              >
                閉じる
              </span>
              {buttonText && handler ? (
                <button
                  type="button"
                  className={`${styles["f-modal__wrapper-bottom__btn"]} ${styles["handle"]}`}
                  style={{ backgroundColor: genBottomBtnColor() }}
                  onClick={() => handler()}
                >
                  {buttonText}
                </button>
              ) : linkText && to ? (
                <SonicLink
                  className={`${styles["f-modal__wrapper-bottom__btn"]} ${styles["link"]}`}
                  to={to}
                  newTab={true}
                >
                  <FontAwesomeIcon icon={["fal", "external-link"]} />
                  {linkText}
                </SonicLink>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Modal;
